import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import { Heart, Calendar, Gift, CheckCircle, ArrowRight, Users, Star, MapPin } from 'react-feather';
import confetti from 'canvas-confetti';
import nigerianWedding from '../asset/leonardo-miranda-dvF6s1H1x68-unsplash.jpg';
import westernWedding from '../asset/inframe-studio-s-eS0xSQXRnMc-unsplash.jpg';
import blendedWedding from '../asset/sandy-millar-8vaQKYnawHw-unsplash.jpg';
import ExampleCouples from '../components/ExamplesCouples';

// Interfaces for type safety
interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface Testimonial {
  quote: string;
  author: string;
  image: string;
}

interface Step {
  number: number;
  title: string;
  description: string;
  image: string;
}

interface Stat {
  icon: React.ReactNode;
  value: number;
  label: string;
  suffix: string;
}

interface HeroImage {
  src: string;
  alt: string;
}

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentFeature, setCurrentFeature] = useState<number>(0);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [currentImage, setCurrentImage] = useState<number>(0);
  const [currentSubheading, setCurrentSubheading] = useState<number>(0);

  // Subheading variants for arrow key cycling
  const subheadings: string[] = [
    'From vibrant Owambes to elegant receptions, create memories that last a lifetime.',
    'Celebrate love with Yoruba traditions or modern elegance, tailored to you.',
    'Plan your dream Owambe or beachside wedding with ease and joy.',
  ];

  // Refs for scroll animations
  const heroRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const testimonialsRef = useRef<HTMLDivElement>(null);
  const examplesRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  // Check if sections are in view
  const heroInView = useInView(heroRef, { once: false, amount: 0.5 });
  const featuresInView = useInView(featuresRef, { once: false, amount: 0.3 });
  const stepsInView = useInView(stepsRef, { once: false, amount: 0.3 });
  const testimonialsInView = useInView(testimonialsRef, { once: false, amount: 0.3 });
  const examplesInView = useInView(examplesRef, { once: false, amount: 0.3 });
  const ctaInView = useInView(ctaRef, { once: false, amount: 0.7 });

  // Scroll progress for parallax effects
  const { scrollYProgress } = useScroll();
  const smoothScroll = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });
  const heroY = useTransform(smoothScroll, [0, 0.5], [0, -100]);
  const heroOpacity = useTransform(smoothScroll, [0, 0.25], [1, 0.25]);

  // Confetti effect for CTA button
  const triggerConfetti = () => {
    const duration = 2 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number): number {
      return Math.random() * (max - min) + min;
    }

    const interval: NodeJS.Timeout = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);
      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#FF6B6B', '#FFD700', '#C0392B', '#E67E22'],
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#FF6B6B', '#FFD700', '#C0392B', '#E67E22'],
      });
    }, 250);
  };

  // Hero image carousel
  const heroImages: HeroImage[] = [
    {
      src: nigerianWedding,
      alt: 'Newlywed African couple in vibrant traditional wedding attire',
    },
    {
      src: westernWedding,
      alt: 'African-American couple in Western-style beach wedding',
    },
    {
      src: blendedWedding,
      alt: 'Blended African and Western wedding celebration with cultural elements',
    },
  ];

  useEffect(() => {
    const imageInterval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(imageInterval);
  }, []);

  // Arrow key handling for subheading cycling
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        setCurrentSubheading((prev) => (prev + 1) % subheadings.length);
      } else if (event.key === 'ArrowLeft') {
        setCurrentSubheading((prev) => (prev - 1 + subheadings.length) % subheadings.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [subheadings.length]);

  const features: Feature[] = [
    {
      icon: <Heart size={32} />,
      title: 'Craft Your Dream Wedding Site',
      description: 'From Aso-Ebi to Kente elegance, create a personalized site for your special day.',
    },
    {
      icon: <Calendar size={32} />,
      title: 'Manage Your Guest List',
      description: 'Track RSVPs, Aso-Ebi preferences, and send reminders for your Owambe or reception.',
    },
    {
      icon: <Gift size={32} />,
      title: 'Gift Registry & Contributions',
      description: 'Create a registry or collect contributions, from cash gifts to traditional items.',
    },
    {
      icon: <CheckCircle size={32} />,
      title: 'Organize Wedding Tasks',
      description: 'Plan your traditional engagement and white wedding with customizable timelines.',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [features.length]);

  const steps: Step[] = [
    {
      number: 1,
      title: 'Sign Up & Create Account',
      description: 'Register for free and set up your wedding profile in minutes.',
      image: 'https://images.pexels.com/photos/10440835/pexels-photo-10440835.jpeg',
    },
    {
      number: 2,
      title: 'Plan Your Traditional Engagement',
      description: 'Organize your Yoruba, Igbo, or other cultural ceremonies with ease.',
      image: 'https://images.pexels.com/photos/11396445/pexels-photo-11396445.jpeg',
    },
    {
      number: 3,
      title: 'Design Your Wedding Website',
      description: 'Choose templates with Ankara or Kente patterns to match your theme.',
      image: 'https://images.pexels.com/photos/3589903/pexels-photo-3589903.jpeg',
    },
    {
      number: 4,
      title: 'Invite Guests & Manage RSVPs',
      description: 'Send digital invitations and track responses for your Owambe or reception.',
      image: 'https://images.pexels.com/photos/10440835/pexels-photo-10440835.jpeg',
    },
  ];

  const testimonials: Testimonial[] = [
    {
      quote: 'This app made our Yoruba engagement and white wedding seamless! The Aso-Ebi coordination was a game-changer.',
      author: 'Funmi & Ade',
      image: 'https://images.pexels.com/photos/11396445/pexels-photo-11396445.jpeg',
    },
    {
      quote: 'Planning our beach wedding with African touches was so easy. The RSVP system was flawless!',
      author: 'Sophie & Liam',
      image: 'https://images.pexels.com/photos/1444442/pexels-photo-1444442.jpeg',
    },
    {
      quote: 'We blended Igbo traditions with a modern reception perfectly using this app!',
      author: 'Chidinma & Mark',
      image: 'https://images.pexels.com/photos/10440835/pexels-photo-10440835.jpeg',
    },
  ];

  const stats: Stat[] = [
    { icon: <Users size={28} />, value: 25000, label: 'Happy Couples', suffix: '+' },
    { icon: <Gift size={28} />, value: 1000000, label: 'Gifts Registered', suffix: '+' },
    { icon: <MapPin size={28} />, value: 180, label: 'Countries', suffix: '' },
    { icon: <Star size={28} />, value: 4.9, label: 'Average Rating', suffix: '/5' },
  ];

  const AnimatedCounter: React.FC<{ value: number; suffix?: string }> = ({ value, suffix = '' }) => {
    const counterRef = useRef<HTMLSpanElement>(null);
    const isInView = useInView(counterRef, { once: true });
    const [count, setCount] = useState<number>(0);

    useEffect(() => {
      if (isInView) {
        let start = 0;
        const duration = 2000;
        const increment = value / (duration / 16);
        const timer = setInterval(() => {
          start += increment;
          if (start >= value) {
            setCount(value);
            clearInterval(timer);
          } else {
            setCount(Math.floor(start));
          }
        }, 16);
        return () => clearInterval(timer);
      }
    }, [isInView, value]);

    return <span ref={counterRef}>{Number.isInteger(value) ? count.toLocaleString() : count.toFixed(1)}{suffix}</span>;
  };

  const handleRegisterClick = () => {
    triggerConfetti();
    setTimeout(() => navigate('/pricing'), 300);
  };

  const handleLoginClick = () => navigate('/login');

  const navigateTo = (path: string) => navigate(path);

  // Adinkra SVG (Gye Nyame) for Hero decorative elements
  const AdinkraIcon = () => (
    <svg
      width="30"
      height="30"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#FFD700"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2c-1.66 0-3 1.34-3 3v3c0 1.66 1.34 3 3 3s3-1.34 3-3V5c0-1.66-1.34-3-3-3z" />
      <path d="M12 11v6" />
      <path d="M9 17h6" />
      <path d="M6 8h3" />
      <path d="M15 8h3" />
    </svg>
  );

  // Cowrie Shell SVG for section decorations
  const CowrieShell = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="#FFD700"
      stroke="#C0392B"
      strokeWidth="1"
    >
      <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9zm0 16c-3.86 0-7-3.14-7-7s3.14-7 7-7 7 3.14 7 7-3.14 7-7 7z" />
      <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z" />
    </svg>
  );

  return (
    <div className="font-sans min-h-screen overflow-hidden font-inter">
      {/* Hero Section with Taller Carousel */}
      <div ref={heroRef} className="relative bg-gradient-to-r from-[#A1005C] via-[#FF6B6B] to-[#FFD700] text-white overflow-hidden">
        <motion.div
          className="absolute inset-0 overflow-hidden opacity-15"
          style={{ y: heroY, opacity: heroOpacity }}
        >
          {/* Ankara-inspired pattern */}
          <div
            className="absolute inset-0 bg-repeat"
            style={{
              backgroundImage: `url('https://www.transparenttextures.com/patterns/flowers.png')`,
            }}
          />
        </motion.div>
        {/* Subtle overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent z-1"></div>
        {/* Adinkra motifs */}
        <motion.div className="absolute inset-0">
          {Array.from({ length: 6 }).map((_, i) => (
            <motion.div
              key={`adinkra-${i}`}
              className="absolute text-[#FFD700]"
              initial={{
                x: `${Math.random() * 100}%`,
                y: `${Math.random() * 100 + 100}%`,
                scale: Math.random() * 0.3 + 0.3,
                opacity: 0,
              }}
              animate={{
                y: [null, '-150%'],
                opacity: [0, 0.2, 0],
                rotate: [0, Math.random() * 90 - 45],
                transition: {
                  repeat: Infinity,
                  duration: Math.random() * 15 + 15,
                  delay: Math.random() * 5,
                  ease: 'easeInOut',
                },
              }}
            >
              <AdinkraIcon />
            </motion.div>
          ))}
        </motion.div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 sm:py-36 relative z-10">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="/assets/logo-gold.png"
              alt="Tales of Two Logo"
              className="h-24 md:h-32 w-auto"
              onError={(e) => (e.currentTarget.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7')}
            />
          </div>

          {/* Taller Image Carousel */}
          <div className="relative h-80 sm:h-[32rem] md:h-[36rem] lg:h-[40rem] mb-12 rounded-2xl overflow-hidden shadow-2xl">
            <AnimatePresence mode="wait">
              <motion.img
                key={currentImage}
                src={heroImages[currentImage].src}
                alt={heroImages[currentImage].alt}
                className="w-full h-full object-cover"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.8 }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) =>
                  (e.currentTarget.src = '/asset/fallback-wedding.jpg')
                }
              />
              {/* Logo watermark on image */}
              <motion.div
                className="absolute bottom-4 right-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.6 }}
                transition={{ delay: 0.5 }}
              >
                <img
                  src="/assets/logo-gold.png"
                  alt="Tales of Two"
                  className="h-12 w-auto"
                  onError={(e) => (e.currentTarget.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7')}
                />
              </motion.div>
            </AnimatePresence>
            {/* Carousel Dots */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {heroImages.map((_, index) => (
                <motion.div
                  key={index}
                  className={`w-3 h-3 rounded-full ${
                    currentImage === index ? 'bg-[#FFD700]' : 'bg-white/50'
                  }`}
                  onClick={() => setCurrentImage(index)}
                  whileHover={{ scale: 1.2 }}
                />
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center bg-white/10 backdrop-blur-md py-8 px-6 rounded-2xl border border-[#FFD700]/50"
          >
            <motion.h1
              className="text-4xl sm:text-6xl lg:text-7xl font-bold font-['Cinzel Decorative'] tracking-tight mb-6 text-[#FFF5F0]"
              style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <span className="inline-block">Wedding Stories,</span>{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FFD700] via-white to-[#FF6B6B]">
                Beautifully Told
              </span>
            </motion.h1>
            <AnimatePresence mode="wait">
              <motion.p
                key={currentSubheading}
                className="text-lg sm:text-2xl max-w-3xl mx-auto text-[#1A3C34] font-semibold leading-relaxed"
                style={{ textShadow: '0 2px 6px rgba(0, 0, 0, 0.4)' }}
                initial={{
                  opacity: 0,
                  x: currentSubheading > 0 ? 20 : -20,
                }}
                animate={{ opacity: 1, x: 0 }}
                exit={{
                  opacity: 0,
                  x: currentSubheading > 0 ? -20 : 20,
                }}
                transition={{ duration: 0.5, ease: 'easeInOut', delay: 0.2 }}
                aria-live="polite"
              >
                {subheadings[currentSubheading]}
              </motion.p>
            </AnimatePresence>
            <motion.div
              className="mt-10"
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <motion.button
                onClick={handleRegisterClick}
                className="relative inline-flex items-center bg-white text-[#FF6B6B] font-bold px-8 py-4 rounded-full shadow-lg text-lg group overflow-hidden border border-[#FFD700]/50"
                style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)' }}
                whileHover={{ scale: 1.05, boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)' }}
                whileTap={{ scale: 0.95 }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <motion.span
                  className="absolute inset-0 bg-[#E67E22]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  initial={{ x: '-100%' }}
                  animate={isHovered ? { x: '0%' } : { x: '-100%' }}
                  transition={{ duration: 0.5 }}
                />
                <span className="relative z-10">Start Planning For Free</span>
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
        <svg
          className="absolute bottom-0 w-full h-24 text-[#FFE8D6] fill-current"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
        >
          <path
            fillOpacity="1"
            d="M0,224L60,218.7C120,213,240,203,360,181.3C480,160,600,128,720,133.3C840,139,960,181,1080,197.3C1200,213,1320,203,1380,197.3L1440,192L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"
          ></path>
        </svg>
      </div>

      {/* Stats Section */}
      <div className="relative bg-gradient-to-b from-[#FFF8E7] to-[#FFE8D6] py-12 border-b border-[#E67E22]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.8 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-[#E67E22]/20 text-[#C0392B]">
                  {stat.icon}
                </div>
                <div className="text-3xl md:text-4xl font-bold text-[#1A3C34]">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="mt-1 text-sm md:text-base text-[#1A3C34]/80">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" ref={featuresRef} className="relative bg-white py-24">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0 bg-repeat"
            style={{ backgroundImage: `url('https://www.transparenttextures.com/patterns/kale-salad.png')` }}
          />
        </div>
        <div className="absolute top-4 left-4">
          <CowrieShell />
        </div>
        <div className="absolute bottom-4 right-4">
          <CowrieShell />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block mb-3 px-4 py-1 rounded-full bg-[#E67E22]/20 text-[#C0392B] text-sm font-medium">
              Everything You Need
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold font-['Cinzel Decorative'] text-[#1A3C34] mb-4">
              Plan Your Dream Wedding
            </h2>
            <motion.div
              className="h-1 w-24 bg-[#C0392B] mx-auto mb-6"
              initial={{ width: 0 }}
              animate={featuresInView ? { width: 96 } : { width: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            />
            <p className="mt-4 text-xl text-[#1A3C34]/80 max-w-3xl mx-auto">
              From Owambes to elegant receptions, our platform makes planning vibrant and seamless.
            </p>
          </motion.div>

          <div className="mt-16 lg:mt-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={featuresInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                transition={{ duration: 0.8 }}
                className="relative h-80 lg:h-96 rounded-2xl overflow-hidden shadow-2xl group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#FFE8D6] to-[#E67E22]/30 rounded-2xl overflow-hidden flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentFeature}
                      initial={{ opacity: 0, y: 20, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -20, scale: 0.9 }}
                      transition={{ duration: 0.5 }}
                      className="text-center px-8 py-4 max-w-sm mx-auto relative z-10"
                    >
                      <motion.div
                        className="flex justify-center text-[#C0392B] mb-6"
                        initial={{ scale: 0.5 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                      >
                        <div className="w-20 h-20 rounded-full bg-[#E67E22]/20 flex items-center justify-center shadow-inner">
                          {features[currentFeature].icon}
                        </div>
                      </motion.div>
                      <motion.h3
                        className="text-2xl md:text-3xl font-bold font-['Cinzel Decorative'] text-[#1A3C34] mb-3"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.3 }}
                      >
                        {features[currentFeature].title}
                      </motion.h3>
                      <motion.p
                        className="mt-3 text-[#1A3C34]/80"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.4 }}
                      >
                        {features[currentFeature].description}
                      </motion.p>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={featuresInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    className={`flex p-5 rounded-xl cursor-pointer transition-all border border-[#FFD700]/50 ${
                      currentFeature === index
                        ? 'bg-[#E67E22]/10 shadow-md'
                        : 'hover:bg-[#FFE8D6]'
                    }`}
                    onClick={() => setCurrentFeature(index)}
                    whileHover={{ scale: 1.02, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1, type: 'spring', stiffness: 300 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={featuresInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  >
                    <div
                      className={`flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-full ${
                        currentFeature === index ? 'bg-[#C0392B] text-white' : 'bg-[#E67E22]/20 text-[#C0392B]'
                      }`}
                    >
                      {feature.icon}
                    </div>
                    <div className="ml-5">
                      <h4 className="text-lg font-semibold text-[#1A3C34]">{feature.title}</h4>
                      <p className="mt-1 text-[#1A3C34]/80">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Balanced Steps Section */}
      <div id="steps" ref={stepsRef} className="relative bg-gradient-to-b from-[#FFF8E7] to-[#FFE8D6] py-24">
        <div className="absolute top-4 left-4">
          <CowrieShell />
        </div>
        <div className="absolute bottom-4 right-4">
          <CowrieShell />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={stepsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block mb-3 px-4 py-1 rounded-full bg-[#E67E22]/20 text-[#C0392B] text-sm font-medium">
              Simple Steps
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold font-['Cinzel Decorative'] text-[#1A3C34] mb-6">
              How It Works
            </h2>
            <p className="mt-4 text-xl text-[#1A3C34]/80 max-w-3xl mx-auto">
              Get started in minutes and create your perfect wedding experience, from Owambe to reception.
            </p>
          </motion.div>

          <div className="relative space-y-12">
            {/* Vertical connecting line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-[#E67E22]/50 hidden md:block"></div>

            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="relative flex flex-col md:flex-row items-center w-full"
                initial={{ opacity: 0, y: 50 }}
                animate={stepsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                {/* Step Image */}
                <div className="w-full md:w-1/2 mb-6 md:mb-0 md:pr-8 h-48">
                  <div className="bg-[#FFF8E7] rounded-xl overflow-hidden shadow-lg transform transition-all hover:scale-105">
                    <motion.div className="relative">
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover"
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) =>
                          (e.currentTarget.src = '/asset/fallback-wedding.jpg')
                        }
                      />
                      {/* Adinkra watermark on images */}
                      <div className="absolute bottom-2 right-2 opacity-40">
                        <AdinkraIcon />
                      </div>
                    </motion.div>
                  </div>
                </div>
                {/* Step Content */}
                <div className="w-full md:w-1/2 flex items-center justify-center md:justify-start">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#C0392B] text-white font-bold mr-4 md:mr-8 shadow-lg">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-['Cinzel Decorative'] text-[#1A3C34] mb-2">{step.title}</h3>
                    <p className="text-[#1A3C34]/80">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="mt-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={stepsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <motion.button
              onClick={handleRegisterClick}
              className="relative inline-flex items-center bg-gradient-to-r from-[#C0392B] to-[#E67E22] text-white font-bold px-8 py-4 rounded-full shadow-lg text-lg group overflow-hidden border border-[#FFD700]/50"
              whileHover={{ scale: 1.05, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)' }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.span
                className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                initial={{ x: '-100%' }}
                animate={{ x: '0%' }}
                transition={{ duration: 0.5 }}
              />
              <span className="relative z-10">Get Started Now</span>
              <motion.div
                className="ml-2"
                initial={{ x: 0 }}
                animate={{ x: [0, 5, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                <ArrowRight size={20} />
              </motion.div>
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div id="testimonials" ref={testimonialsRef} className="relative bg-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0 bg-repeat"
            style={{ backgroundImage: `url('https://www.transparenttextures.com/patterns/kale-salad.png')` }}
          />
        </div>
        <div className="absolute top-4 left-4">
          <CowrieShell />
        </div>
        <div className="absolute bottom-4 right-4">
          <CowrieShell />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block mb-3 px-4 py-1 rounded-full bg-[#E67E22]/20 text-[#C0392B] text-sm font-medium">
              Testimonials
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold font-['Cinzel Decorative'] text-[#1A3C34] mb-4">
              Loved by Couples Worldwide
            </h2>
            <motion.div
              className="h-1 w-24 bg-[#C0392B] mx-auto mb-6"
              initial={{ width: 0 }}
              animate={testimonialsInView ? { width: 96 } : { width: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            />
            <p className="mt-4 text-xl text-[#1A3C34]/80 max-w-2xl mx-auto">
              From Lagos to London, hear from couples who planned their perfect day.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl shadow-xl overflow-hidden border border-[#FFD700]/50 hover:shadow-2xl transition-shadow duration-500"
                initial={{ opacity: 0, y: 50 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ y: -8 }}
              >
                <div className="h-48 overflow-hidden relative">
                  <img
                    src={testimonial.image}
                    alt={`Wedding of ${testimonial.author}`}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) =>
                      (e.currentTarget.src = '/asset/fallback-wedding.jpg')
                    }
                  />
                  {/* Adinkra watermark on images */}
                  <div className="absolute bottom-2 right-2 opacity-40">
                    <AdinkraIcon />
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <motion.svg
                        key={i}
                        className="w-5 h-5 text-[#FFD700] fill-current"
                        viewBox="0 0 24 24"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={testimonialsInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                        transition={{ duration: 0.3, delay: 0.7 + index * 0.2 + i * 0.1 }}
                      >
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"></path>
                      </motion.svg>
                    ))}
                  </div>
                  <p className="text-[#1A3C34]/80 italic mb-4">"{testimonial.quote}"</p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-[#E67E22]/20 mr-3 flex items-center justify-center text-[#C0392B]">
                      <Heart size={16} />
                    </div>
                    <p className="font-medium text-[#C0392B]">{testimonial.author}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Example Couples Section */}
      <div id="examples" ref={examplesRef} className="relative bg-gradient-to-b from-[#FFF8E7] to-[#FFE8D6] py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0 bg-repeat"
            style={{ backgroundImage: `url('https://www.transparenttextures.com/patterns/kale-salad.png')` }}
          />
        </div>
        <div className="absolute top-4 left-4">
          <CowrieShell />
        </div>
        <div className="absolute bottom-4 right-4">
          <CowrieShell />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={examplesInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block mb-3 px-4 py-1 rounded-full bg-[#E67E22]/20 text-[#C0392B] text-sm font-medium">
              Success Stories
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold font-['Cinzel Decorative'] text-[#1A3C34] mb-4">
              Real Couples, Real Love Stories
            </h2>
            <motion.div
              className="h-1 w-24 bg-[#C0392B] mx-auto mb-6"
              initial={{ width: 0 }}
              animate={examplesInView ? { width: 96 } : { width: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            />
            <p className="mt-4 text-xl text-[#1A3C34]/80 max-w-3xl mx-auto">
              Browse these beautiful wedding websites created with Tales of Two
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                names: 'Amara & David',
                date: 'August 2024',
                location: 'Lagos, Nigeria',
                theme: 'Traditional Yoruba with modern elements',
                image: 'https://images.pexels.com/photos/3589903/pexels-photo-3589903.jpeg',
                url: '/wedding/amara-david'
              },
              {
                names: 'Jessica & Michael',
                date: 'June 2024',
                location: 'Accra, Ghana',
                theme: 'Beachside ceremony with cultural touches',
                image: 'https://images.pexels.com/photos/1444442/pexels-photo-1444442.jpeg',
                url: '/wedding/jessica-michael'
              },
              {
                names: 'Priya & Raj',
                date: 'October 2024',
                location: 'Durban, South Africa',
                theme: 'Fusion of Indian and African traditions',
                image: 'https://images.pexels.com/photos/2960556/pexels-photo-2960556.jpeg',
                url: '/wedding/priya-raj'
              }
            ].map((couple, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl shadow-xl overflow-hidden border border-[#FFD700]/50 hover:shadow-2xl transition-all duration-500"
                initial={{ opacity: 0, y: 50 }}
                animate={examplesInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ y: -8, scale: 1.02 }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={couple.image}
                    alt={`Wedding of ${couple.names}`}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) =>
                      (e.currentTarget.src = '/asset/fallback-wedding.jpg')
                    }
                  />
                  {/* Gradient overlay with Adinkra watermark */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end justify-end p-4">
                    <AdinkraIcon />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <h3 className="text-xl font-bold">{couple.names}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-[#1A3C34]">
                      <Calendar size={16} className="mr-2 text-[#C0392B]" />
                      <span>{couple.date}</span>
                    </div>
                    <div className="flex items-center text-[#1A3C34]">
                      <MapPin size={16} className="mr-2 text-[#C0392B]" />
                      <span>{couple.location}</span>
                    </div>
                  </div>
                  <p className="text-[#1A3C34]/80 mb-4">{couple.theme}</p>
                  <a
                    href={couple.url}
                    className="inline-flex items-center text-[#C0392B] font-medium hover:text-[#E67E22] transition-colors"
                  >
                    View their wedding site
                    <ArrowRight size={16} className="ml-1" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA for viewing more examples */}
          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={examplesInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <motion.button
              onClick={handleRegisterClick}
              className="relative inline-flex items-center bg-gradient-to-r from-[#C0392B] to-[#E67E22] text-white font-bold px-8 py-4 rounded-full shadow-lg text-lg group overflow-hidden border border-[#FFD700]/50"
              whileHover={{ scale: 1.05, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)' }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.span
                className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                initial={{ x: '-100%' }}
                animate={{ x: '0%' }}
                transition={{ duration: 0.5 }}
              />
              <span className="relative z-10">Create Your Wedding Website</span>
              <motion.div
                className="ml-2"
                initial={{ x: 0 }}
                animate={{ x: [0, 5, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                <ArrowRight size={20} />
              </motion.div>
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* App Screenshot Showcase */}
      <div className="relative bg-gradient-to-b from-[#FFF8E7] to-[#FFE8D6] py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="inline-block mb-3 px-4 py-1 rounded-full bg-[#E67E22]/20 text-[#C0392B] text-sm font-medium">
              Beautiful Interface
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold font-['Cinzel Decorative'] text-[#1A3C34] mb-6">
              Elegantly Designed for You
            </h2>
            <p className="mt-4 text-xl text-[#1A3C34]/80 max-w-3xl mx-auto">
              Our intuitive interface makes wedding planning a delightful experience.
            </p>
          </motion.div>

          <div className="relative">
            <motion.div
              className="absolute top-1/2 left-1/2 w-[140%] h-[140%] bg-gradient-to-r from-[#E67E22]/30 to-[#C0392B]/30 rounded-full blur-3xl -z-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
              style={{ transform: 'translate(-50%, -50%)' }}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <motion.div
                className="md:col-span-2 relative"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="relative mx-auto max-w-lg rounded-2xl shadow-2xl overflow-hidden border-8 border-white bg-[#1A3C34]">
                  <img
                    src="https://images.pexels.com/photos/3185483/pexels-photo-3185483.jpeg"
                    alt="Wedding app dashboard"
                    className="w-full h-auto"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) =>
                      (e.currentTarget.src = '/asset/fallback-wedding.jpg')
                    }
                  />
                  <div className="absolute top-4 left-4 flex space-x-2">
                    <div className="w-3 h-3 bg-[#C0392B] rounded-full"></div>
                    <div className="w-3 h-3 bg-[#FFD700] rounded-full"></div>
                    <div className="w-3 h-3 bg-[#E67E22] rounded-full"></div>
                  </div>
                  {/* Adinkra watermark on screenshot */}
                  <div className="absolute bottom-4 right-4 opacity-50">
                    <AdinkraIcon />
                  </div>
                </div>
              </motion.div>
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                {[
                  { title: 'Modern & Clean Design', description: 'Intuitive interface with vibrant or elegant themes.' },
                  { title: 'Mobile Responsive', description: 'Access your plans from any device, anywhere.' },
                  { title: 'Customizable Themes', description: 'Choose Ankara or Kente patterns.' },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    className="bg-white p-6 rounded-xl shadow-md border border-[#FFD700]/50"
                    whileHover={{ scale: 1.02 }}
                  >
                    <h3 className="text-xl font-bold text-[#1A3C34] mb-2">{item.title}</h3>
                    <p className="text-[#1A3C34]/80">{item.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div ref={ctaRef} className="relative bg-gradient-to-r from-[#C0392B] to-[#E67E22] text-white py-16 overflow-hidden">
        <div className="absolute inset-0">
          {Array.from({ length: 20 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              initial={{
                x: `${Math.random() * 100}%`,
                y: `${Math.random() * 100}%`,
                opacity: 0.1,
              }}
              animate={{
                opacity: [0.1, 0.3, 0.1],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: Math.random() * 5 + 5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <CowrieShell />
            </motion.div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2
              className="text-3xl sm:text-5xl font-bold font-['Cinzel Decorative'] mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={ctaInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Start Planning Your Perfect Wedding Today
            </motion.h2>
            <motion.p
              className="text-xl max-w-3xl mx-auto mb-10 text-[#FFE8D6]"
              initial={{ opacity: 0, y: 20 }}
              animate={ctaInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Join thousands of happy couples who have created their dream wedding with Tales of Two.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={ctaInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.button
                onClick={handleRegisterClick}
                className="inline-flex items-center bg-white text-[#C0392B] font-bold px-8 py-4 rounded-full shadow-lg text-lg group relative overflow-hidden border border-[#FFD700]/50"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="absolute inset-0 bg-[#E67E22]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative z-10">Get Started for Free</span>
              </motion.button>
              <p className="mt-4 text-sm text-[#FFE8D6]">No credit card required</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1A3C34] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="space-y-4">
              {/* Tales of Two Logo */}
              <div className="mb-6">
                <img
                  src="/assets/logo-gold.png"
                  alt="Tales of Two Logo"
                  className="h-16 w-auto"
                  onError={(e) => (e.currentTarget.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7')}
                />
              </div>
              <p className="text-[#FFE8D6]">
                Wedding Stories, Beautifully Told. Making wedding planning vibrant and elegant for couples worldwide.
              </p>
              <div className="flex space-x-4 mt-6">
                {[
                  { name: 'facebook', icon: <motion.svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></motion.svg> },
                  { name: 'twitter', icon: <motion.svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path></motion.svg> },
                  { name: 'instagram', icon: <motion.svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></motion.svg> },
                  { name: 'pinterest', icon: <motion.svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 12a4 4 0 0 1 8 0c0 2.667-2 4-4 9-2-5-4-6.333-4-9z"/></motion.svg> }
                ].map((social) => (
                  <motion.a
                    key={social.name}
                    href={`#${social.name}`}
                    className="w-10 h-10 rounded-full bg-[#FFE8D6]/20 flex items-center justify-center text-[#E67E22] hover:bg-[#C0392B] hover:text-white transition-colors duration-300"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="sr-only">{social.name}</span>
                    {social.icon}
                    <CowrieShell />
                  </motion.a>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6 text-white">Features</h4>
              <ul className="space-y-3 text-[#FFE8D6]">
                {['Wedding Website', 'Guest Management', 'Gift Registry', 'Wedding Fund', 'Photo Gallery', 'RSVP Management'].map(
                  (item) => (
                    <motion.li
                      key={item}
                      whileHover={{ x: 5, color: '#FFD700' }}
                      className="hover:text-[#FFD700] cursor-pointer transition-colors"
                    >
                      {item}
                    </motion.li>
                  )
                )}
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6 text-white">Company</h4>
              <ul className="space-y-3 text-[#FFE8D6]">
                {[
                  { name: 'About Us', path: '/about' },
                  { name: 'Contact', path: '/contact' },
                  { name: 'Example Weddings', path: '/examples' },
                  { name: 'Privacy Policy', path: '/privacy' },
                  { name: 'Terms of Service', path: '/terms' },
                  { name: 'Careers', path: '/careers' },
                ].map((item) => (
                  <motion.li
                    key={item.name}
                    whileHover={{ x: 5, color: '#FFD700' }}
                    className="hover:text-[#FFD700] cursor-pointer transition-colors"
                    onClick={() => navigateTo(item.path)}
                  >
                    {item.name}
                  </motion.li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6 text-white">Support</h4>
              <ul className="space-y-3 text-[#FFE8D6]">
                {[
                  { name: 'Help Center', path: '/help' },
                  { name: 'FAQs', path: '/faqs' },
                  { name: 'Contact Support', path: '/support' },
                  { name: 'Community', path: '/community' },
                ].map((item) => (
                  <motion.li
                    key={item.name}
                    whileHover={{ x: 5, color: '#FFD700' }}
                    className="hover:text-[#FFD700] cursor-pointer transition-colors"
                    onClick={() => navigateTo(item.path)}
                  >
                    {item.name}
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-[#FFE8D6]/20 text-center text-[#FFE8D6]/80">
            <p>&copy; {new Date().getFullYear()} Tales of Two. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;